import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { LucideAngularModule, Bell, ChevronDown } from 'lucide-angular';
import { ThemeService } from '../../services/theme.service';
import { FullscreenService } from '../../services/fullscreen.service';
import { TenantService } from '../../services/tenant.service';
import { NavItemComponent } from './nav-item';
import { ModeToggleComponent } from './mode-toggle';
import { NavDropdownComponent } from './nav-dropdown';
import {
  NavItem,
  OPERATIONS_NAV_ITEMS,
  SETTINGS_NAV_ITEMS,
  withEnabledChildren,
} from './nav-config';
import { isFeatureEnabled } from '../../config/runtime-config';

@Component({
  selector: 'sh-navbar',
  imports: [
    RouterLink,
    LucideAngularModule,
    NavItemComponent,
    ModeToggleComponent,
    NavDropdownComponent,
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'block w-full',
    '(window:scroll)': 'onScroll()',
    '(document:keydown.escape)': 'onEscape()',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class AppTopNavbarComponent implements AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly doc = inject(DOCUMENT);
  protected readonly theme = inject(ThemeService);
  protected readonly fullscreen = inject(FullscreenService);
  private readonly keycloak = inject(Keycloak);
  private readonly tenantService = inject(TenantService);

  // Icons
  protected readonly bellIcon = Bell;
  protected readonly chevronDownIcon = ChevronDown;

  // State
  protected readonly isSettingsMode = signal(false);
  protected readonly activeTabId = signal('dashboard');

  /** Computed avatar initials from Keycloak token profile */
  protected readonly avatarInitials = computed(() => {
    const profile = this.keycloak.tokenParsed;
    if (!profile) return '??';
    const first = (profile['given_name'] as string)?.[0] ?? '';
    const last = (profile['family_name'] as string)?.[0] ?? '';
    return (first + last).toUpperCase() || (profile['preferred_username'] as string)?.[0]?.toUpperCase() || '??';
  });
  protected readonly isScrolled = signal(false);
  protected readonly mobileMenuOpen = signal(false);
  protected readonly userMenuOpen = signal(false);

  // Mobile expanded sections (track which parent tabs have their children expanded)
  protected readonly mobileExpandedIds = signal<Set<string>>(new Set());

  // Pill position
  protected readonly pillLeft = signal(0);
  protected readonly pillWidth = signal(0);

  // Computed
  protected readonly navItems = computed<NavItem[]>(() =>
    withEnabledChildren(this.isSettingsMode() ? SETTINGS_NAV_ITEMS : OPERATIONS_NAV_ITEMS, isFeatureEnabled),
  );

  protected readonly pillLeftPx = computed(() => `${this.pillLeft()}px`);
  protected readonly pillWidthPx = computed(() => `${this.pillWidth()}px`);

  // View references
  @ViewChildren(NavItemComponent, { read: ElementRef })
  private navItemEls!: QueryList<ElementRef<HTMLElement>>;

  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    // Measure pill on initial render
    queueMicrotask(() => this.updatePillPosition());

    // Re-measure when nav items change (mode switch)
    this.navItemEls.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        queueMicrotask(() => this.updatePillPosition());
      });

    // Listen for route changes to sync active tab
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.syncActiveTabFromRoute());

    // Initial route sync
    this.syncActiveTabFromRoute();

    // Observe container resize for pill recalculation
    const navContainer = this.doc.querySelector('.navbar__capsule-nav');
    if (navContainer && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updatePillPosition());
      this.resizeObserver.observe(navContainer);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.doc.body.classList.remove('overflow-hidden');
  }

  // ── Mode toggle ──────────────────────────────────────────────────
  protected onModeChanged(isSettings: boolean): void {
    this.isSettingsMode.set(isSettings);

    // Set first tab of new mode as active
    const items = isSettings ? SETTINGS_NAV_ITEMS : OPERATIONS_NAV_ITEMS;
    if (items.length > 0) {
      this.activeTabId.set(items[0].id);
      this.router.navigate([items[0].route]);
    }

    // Re-measure pill after mode switch
    setTimeout(() => this.updatePillPosition(), 50);
  }

  // ── Tab activation ───────────────────────────────────────────────
  protected onTabActivated(item: NavItem): void {
    this.activeTabId.set(item.id);
    setTimeout(() => this.updatePillPosition(), 0);
  }

  protected isTabActive(item: NavItem): boolean {
    return this.activeTabId() === item.id;
  }

  // ── Mobile menu ──────────────────────────────────────────────────
  protected toggleMobileMenu(): void {
    const open = !this.mobileMenuOpen();
    this.mobileMenuOpen.set(open);
    this.doc.body.classList.toggle('overflow-hidden', open);
  }

  protected closeMobileMenu(): void {
    if (!this.mobileMenuOpen()) return;
    this.mobileMenuOpen.set(false);
    this.doc.body.classList.remove('overflow-hidden');
  }

  protected toggleMobileSection(itemId: string): void {
    const current = new Set(this.mobileExpandedIds());
    if (current.has(itemId)) {
      current.delete(itemId);
    } else {
      current.add(itemId);
    }
    this.mobileExpandedIds.set(current);
  }

  protected isMobileExpanded(itemId: string): boolean {
    return this.mobileExpandedIds().has(itemId);
  }

  protected onMobileItemClick(item: NavItem): void {
    this.activeTabId.set(item.id);
    this.closeMobileMenu();
    this.router.navigate([item.route]);
    setTimeout(() => this.updatePillPosition(), 50);
  }

  protected onMobileChildClick(route: string): void {
    this.closeMobileMenu();
    this.router.navigate([route]);
  }

  // ── User menu ────────────────────────────────────────────────────
  protected toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
  }

  protected goToSettingsFromMenu(): void {
    this.userMenuOpen.set(false);
    this.isSettingsMode.set(true);
    const firstSettings = SETTINGS_NAV_ITEMS[0];
    if (firstSettings) {
      this.activeTabId.set(firstSettings.id);
      this.router.navigate([firstSettings.route]);
    }
    setTimeout(() => this.updatePillPosition(), 50);
  }

  // ── Global handlers ──────────────────────────────────────────────
  protected onScroll(): void {
    this.isScrolled.set(window.scrollY > 2);
  }

  protected onEscape(): void {
    if (this.mobileMenuOpen()) {
      this.closeMobileMenu();
    }
    if (this.userMenuOpen()) {
      this.userMenuOpen.set(false);
    }
  }

  protected onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;

    // Close user menu if click is outside
    if (this.userMenuOpen()) {
      const userMenuEl = this.doc.querySelector('.navbar__user-menu');
      if (userMenuEl && !userMenuEl.contains(target)) {
        this.userMenuOpen.set(false);
      }
    }
  }

  protected toggleTheme(): void {
    this.theme.toggleMode();
  }

  /** Fullscreen the whole app (navbar stays visible) and close the user menu. */
  protected toggleFullscreen(): void {
    this.fullscreen.toggle();
    this.userMenuOpen.set(false);
  }

  protected signOut(): void {
    // Logout is a full-page redirect, so the in-memory caches die with the page.
    // Dropping the tenant is what stops them serving the old session if it doesn't.
    this.tenantService.clear();
    this.keycloak.logout({ redirectUri: window.location.origin });
  }

  // ── Pill measurement ─────────────────────────────────────────────
  private updatePillPosition(): void {
    const items = this.navItemEls?.toArray() ?? [];
    const activeId = this.activeTabId();
    const navItems = this.navItems();
    const activeIndex = navItems.findIndex((item) => item.id === activeId);

    if (activeIndex < 0 || activeIndex >= items.length) return;

    const btnEl = items[activeIndex]?.nativeElement;
    const navEl = btnEl?.closest('.navbar__capsule-nav') as HTMLElement | null;

    if (!btnEl || !navEl) return;

    const navRect = navEl.getBoundingClientRect();
    const btnRect = btnEl.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.pillLeft.set(btnRect.left - navRect.left);
      this.pillWidth.set(btnRect.width);
    });
  }

  // ── Route sync ───────────────────────────────────────────────────
  private syncActiveTabFromRoute(): void {
    const url = this.router.url;

    // Check settings routes first
    if (url.startsWith('/settings')) {
      if (!this.isSettingsMode()) {
        this.isSettingsMode.set(true);
      }

      // /settings/me is a personal profile page — no settings tab should be highlighted
      if (url === '/settings/me' || url.startsWith('/settings/me/')) {
        this.activeTabId.set('');
        setTimeout(() => this.updatePillPosition(), 50);
        return;
      }

      const match = this.findMatchingTab(SETTINGS_NAV_ITEMS, url);
      if (match) {
        this.activeTabId.set(match.id);
      }
      setTimeout(() => this.updatePillPosition(), 50);
      return;
    }

    // Check operations routes
    if (this.isSettingsMode()) {
      this.isSettingsMode.set(false);
    }
    const match = this.findMatchingTab(OPERATIONS_NAV_ITEMS, url);
    if (match) {
      this.activeTabId.set(match.id);
    }
    setTimeout(() => this.updatePillPosition(), 50);
  }

  private findMatchingTab(items: NavItem[], url: string): NavItem | null {
    // First check children for exact/prefix match
    for (const item of items) {
      if (item.children) {
        for (const child of item.children) {
          if (url === child.route || url.startsWith(child.route + '/')) {
            return item;
          }
        }
      }
    }

    // Then check parent routes
    for (const item of items) {
      if (url === item.route || url.startsWith(item.route + '/')) {
        return item;
      }
    }

    return null;
  }
}
